import React from 'react';
import MaterialIcon from '@/components/ui/MaterialIcon';
import { CoverageStatus } from '@/services/benefitService';

interface CoverageBannerProps {
  status: CoverageStatus;
  message?: string;
  /** Compact variant for the dashboard (no body text, single line). */
  compact?: boolean;
}

const STATUS_META: Record<Exclude<CoverageStatus, 'active'>, {
  title: string;
  defaultMessage: string;
  icon: string;
  bg: string;
  iconBg: string;
  text: string;
}> = {
  suspended: {
    title: 'Couverture suspendue',
    defaultMessage: 'Votre couverture est actuellement suspendue. Contactez votre gestionnaire pour plus d\'informations.',
    icon: 'warning',
    bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50',
    iconBg: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    text: 'text-orange-900 dark:text-orange-100',
  },
  terminated: {
    title: 'Couverture résiliée',
    defaultMessage: 'Votre couverture a été résiliée. Vous ne pouvez plus utiliser le tiers-payant ni soumettre de nouvelles demandes.',
    icon: 'cancel',
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50',
    iconBg: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    text: 'text-red-900 dark:text-red-100',
  },
  no_contract: {
    title: 'Aucun contrat actif',
    defaultMessage: 'Aucun contrat n\'est associé à votre compte pour le moment. Contactez votre gestionnaire.',
    icon: 'info',
    bg: 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700',
    iconBg: 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    text: 'text-gray-900 dark:text-gray-100',
  },
};

const CoverageBanner: React.FC<CoverageBannerProps> = ({ status, message, compact = false }) => {
  if (status === 'active') return null;
  const meta = STATUS_META[status];

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-xl border p-4 ${meta.bg}`}
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.iconBg}`}>
        <MaterialIcon icon={meta.icon} style={{ fontSize: 20 }} />
      </div>
      <div className={`flex-1 ${meta.text}`}>
        <p className="font-bold text-sm leading-tight">{meta.title}</p>
        {!compact && (
          <p className="text-xs mt-1 leading-snug opacity-90">
            {message ?? meta.defaultMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default CoverageBanner;
